import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Space,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AiOutlinePlus,
  AiOutlineReload,
  AiOutlineWarning,
} from "react-icons/ai";

import type { SignupListType } from "shell/console/types";

import { useCustomNotification } from "design-system/hooks";
import {
  useAddSandboxSignupDomain,
  useRemoveSandboxSignupDomain,
  useSandboxAdminSignupDomainLists,
} from "shell/console/api/sandboxAdmin/hooks";
import { SandboxAdminTableLayout } from "shell/console/features/shared/SandboxAdminTableLayout";
import { extractRestErrorMessage } from "shell/platform/utils/utils";

import {
  DOMAIN_PATTERN,
  getActiveListDomains,
  getOtherListDomains,
  normalizeDomain,
} from "./utils";

const { Text } = Typography;

interface DomainRow {
  domain: string;
}

export const DomainLists = () => {
  const { t } = useTranslation("common");
  const { showNotification } = useCustomNotification();

  const [activeList, setActiveList] = useState<SignupListType>("allow");
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [enteredDomain, setEnteredDomain] = useState("");
  const [form] = Form.useForm<{ domain: string }>();

  const { data, isLoading, error, refetch, isFetching } =
    useSandboxAdminSignupDomainLists();
  const { mutateAsync: addDomain, isPending: isAdding } =
    useAddSandboxSignupDomain();
  const { mutateAsync: removeDomain, isPending: isRemoving } =
    useRemoveSandboxSignupDomain();

  const lists = data ?? { allow: [], block: [] };
  const activeDomains = getActiveListDomains(lists, activeList);
  const otherDomains = getOtherListDomains(lists, activeList);

  const query = search.trim().toLowerCase();
  const filteredDomains = query
    ? activeDomains.filter(domain => domain.includes(query))
    : activeDomains;
  const tableData: DomainRow[] = filteredDomains.map(domain => ({ domain }));

  const queryErrorMessage = error
    ? extractRestErrorMessage(error) || error.message || t("errors.unknown")
    : null;

  const listLabel = (list: SignupListType) =>
    t(`components.sandboxAdmin.domainLists.${list}Tab`);

  const otherList: SignupListType = activeList === "allow" ? "block" : "allow";

  const normalizedEntry = enteredDomain ? normalizeDomain(enteredDomain) : "";

  const isDuplicate = Boolean(
    normalizedEntry && activeDomains.includes(normalizedEntry),
  );

  const isConflict = Boolean(
    normalizedEntry && otherDomains.includes(normalizedEntry),
  );

  const duplicateMessage = isDuplicate
    ? t("components.sandboxAdmin.domainLists.alreadyInList", {
        list: listLabel(activeList),
      })
    : null;

  const conflictMessage = isConflict
    ? t("components.sandboxAdmin.domainLists.conflictWarning", {
        otherList: listLabel(otherList),
      })
    : null;

  const warningMessage = duplicateMessage ?? conflictMessage;

  const handleOpenAddModal = () => {
    form.resetFields();
    setEnteredDomain("");
    setIsModalOpen(true);
  };

  const handleSubmit = async ({ domain }: { domain: string }) => {
    const entry = normalizeDomain(domain);
    if (activeDomains.includes(entry)) {
      form.setFields([
        {
          name: "domain",
          errors: [
            t("components.sandboxAdmin.domainLists.alreadyInList", {
              list: listLabel(activeList),
            }),
          ],
        },
      ]);
      return;
    }
    try {
      await addDomain({ list: activeList, entry });
      showNotification({
        type: "success",
        message: t("components.sandboxAdmin.domainLists.addedSuccess"),
        description: t(
          "components.sandboxAdmin.domainLists.addedSuccessDescription",
          { domain: entry, list: listLabel(activeList) },
        ),
      });
      setIsModalOpen(false);
      await refetch();
    } catch (err) {
      showNotification({
        type: "error",
        message: t("components.sandboxAdmin.domainLists.addFailed"),
        description: extractRestErrorMessage(err) ?? "",
      });
    }
  };

  const handleRemove = (domain: string) => {
    Modal.confirm({
      title: t("components.sandboxAdmin.domainLists.removeConfirmTitle"),
      content: t("components.sandboxAdmin.domainLists.removeConfirmContent", {
        domain,
        list: listLabel(activeList),
      }),
      okText: t("components.buttons.remove"),
      okType: "danger",
      onOk: async () => {
        try {
          await removeDomain({ list: activeList, entry: domain });
          showNotification({
            type: "success",
            message: t("components.sandboxAdmin.domainLists.removedSuccess"),
            description: t(
              "components.sandboxAdmin.domainLists.removedSuccessDescription",
              { domain, list: listLabel(activeList) },
            ),
          });
          await refetch();
        } catch (err) {
          showNotification({
            type: "error",
            message: t("components.sandboxAdmin.domainLists.removeFailed"),
            description: extractRestErrorMessage(err) ?? "",
          });
        }
      },
    });
  };

  const toolbar = (
    <Tabs
      activeKey={activeList}
      onChange={value => {
        setActiveList(value as SignupListType);
        setSearch("");
      }}
      items={[
        {
          key: "allow",
          label: `${t("components.sandboxAdmin.domainLists.allowTab")} (${lists.allow.length})`,
        },
        {
          key: "block",
          label: `${t("components.sandboxAdmin.domainLists.blockTab")} (${lists.block.length})`,
        },
      ]}
      data-testid="domain-list-tabs"
    />
  );

  const rightActions = [
    <Button
      key="add"
      type="primary"
      icon={<AiOutlinePlus />}
      onClick={handleOpenAddModal}
      data-testid="add-domain"
    >
      {t("components.sandboxAdmin.domainLists.addDomain")}
    </Button>,
    <Button
      key="refresh"
      icon={<AiOutlineReload />}
      onClick={() => refetch()}
      loading={isFetching}
      data-testid="refresh-domain-lists"
    >
      {t("components.buttons.refresh")}
    </Button>,
  ];

  const columns = [
    {
      title: t("components.sandboxAdmin.domainLists.domainLabel"),
      dataIndex: "domain",
      key: "domain",
      render: (domain: string) => (
        <Space>
          <Text code>{domain}</Text>
          {otherDomains.includes(domain) && (
            <Tag color="warning" icon={<AiOutlineWarning />}>
              {t(
                `components.sandboxAdmin.domainLists.${
                  activeList === "allow" ? "alsoBlocked" : "alsoAllowed"
                }`,
              )}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t("labels.actions"),
      key: "actions",
      align: "center" as const,
      render: (_: unknown, record: DomainRow) => (
        <Button
          size="small"
          danger
          loading={isRemoving}
          onClick={() => handleRemove(record.domain)}
          data-testid={`remove-${record.domain}`}
        >
          {t("components.buttons.remove")}
        </Button>
      ),
    },
  ];

  return (
    <>
      <SandboxAdminTableLayout
        title={t("components.sandboxAdmin.domainLists.title")}
        subtitle={t("components.sandboxAdmin.domainLists.subtitle")}
        toolbar={toolbar}
        data={tableData}
        columns={columns}
        loading={isLoading}
        rowKey={record => record.domain}
        errorMessage={queryErrorMessage}
        onRetry={() => refetch()}
        retryLoading={isFetching}
        rightActions={rightActions}
        searchInput={{
          placeholder: t(
            "components.sandboxAdmin.domainLists.searchPlaceholder",
          ),
          value: search,
          onChange: setSearch,
          testId: "search-domains",
        }}
        renderEmpty={() => (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("components.sandboxAdmin.domainLists.empty")}
          />
        )}
        refreshTestId="refresh-domain-lists"
        testId="sandbox-domain-lists-table"
      />
      {isModalOpen && (
        <Modal
          title={t("components.sandboxAdmin.domainLists.addTitle", {
            list: listLabel(activeList),
          })}
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          onOk={() => form.submit()}
          okText={t("components.sandboxAdmin.domainLists.addDomain")}
          confirmLoading={isAdding}
          centered
          data-testid="add-domain-modal"
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={changedValues => {
              if ("domain" in changedValues) {
                setEnteredDomain(changedValues.domain ?? "");
              }
            }}
            data-testid="add-domain-form"
          >
            <Form.Item
              name="domain"
              label={t("components.sandboxAdmin.domainLists.domainLabel")}
              rules={[
                {
                  required: true,
                  message: t(
                    "components.sandboxAdmin.domainLists.invalidDomain",
                  ),
                },
                {
                  pattern: DOMAIN_PATTERN,
                  message: t(
                    "components.sandboxAdmin.domainLists.invalidDomain",
                  ),
                },
              ]}
            >
              <Input
                placeholder={t(
                  "components.sandboxAdmin.domainLists.addPlaceholder",
                )}
                autoFocus
                data-testid="domain-input"
              />
            </Form.Item>
            {warningMessage && (
              <Alert
                type="warning"
                showIcon
                description={warningMessage}
                data-testid={
                  isDuplicate ? "duplicate-warning" : "conflict-warning"
                }
              />
            )}
          </Form>
        </Modal>
      )}
    </>
  );
};
